import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";
import { Divider } from "./Ornaments";
import { PHOTOS } from "./data";

const PortraitCard = ({ photo, index, onOpen }) => (
    <Reveal delay={0.12 * index}>
        <figure className="group">
            <div className="relative">
                <span
                    className="pointer-events-none absolute -inset-2.5 rounded-t-[10rem] border border-gold/20 transition-colors duration-700 group-hover:border-gold/45"
                    aria-hidden="true"
                />
                <span
                    className="absolute -top-3 left-1/2 z-20 -translate-x-1/2 rotate-45 border border-gold/80 bg-obsidian p-[3px] transition-transform duration-700 group-hover:scale-125"
                    aria-hidden="true"
                >
                    <span className="block h-1.5 w-1.5 bg-gold" />
                </span>
                <button
                    data-testid={`gallery-photo-${index}`}
                    onClick={() => onOpen(photo)}
                    className="relative block w-full cursor-pointer overflow-hidden rounded-t-[9rem] border border-gold/35 bg-royal-card shadow-[0_0_40px_rgba(229,184,42,0.12)] transition-all duration-700 hover:border-gold/70 hover:shadow-[0_0_65px_rgba(229,184,42,0.28)]"
                    aria-label={`View photo: ${photo.caption}`}
                >
                    <span className="pointer-events-none absolute inset-2 z-10 rounded-t-[8rem] border border-gold/20 transition-colors duration-700 group-hover:border-gold/45" />
                    <img
                        src={photo.src}
                        alt={photo.alt}
                        loading="lazy"
                        className="h-[380px] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-110 sm:h-[440px]"
                    />
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-60 transition-opacity duration-700 group-hover:opacity-30" />
                </button>
            </div>
            <figcaption className="mt-5 flex items-center justify-center gap-3 font-royal text-lg italic text-sand">
                <span className="h-px w-8 bg-gradient-to-r from-transparent to-gold/60" aria-hidden="true" />
                {photo.caption}
                <span className="h-px w-8 bg-gradient-to-l from-transparent to-gold/60" aria-hidden="true" />
            </figcaption>
        </figure>
    </Reveal>
);

const Gallery = () => {
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!selected) return;
        const onKey = (e) => e.key === "Escape" && setSelected(null);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selected]);

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="relative overflow-hidden py-24 sm:py-36"
        >
            <div
                className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] max-w-full -translate-x-1/2 rounded-full bg-gold/[0.08] blur-3xl"
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-6xl px-6">
                <Reveal>
                    <p className="text-center font-cinzel text-[11px] font-semibold uppercase tracking-[0.5em] text-gold">
                        Captured Moments
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 text-center font-royal text-3xl font-medium text-ivory sm:text-4xl">
                        Moments Before Forever
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <Divider className="mb-16 mt-8" />
                </Reveal>

                <div className="grid gap-8 sm:grid-cols-3">
                    {PHOTOS.map((photo, index) => (
                        <PortraitCard
                            key={photo.src}
                            photo={photo}
                            index={index}
                            onOpen={setSelected}
                        />
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {selected && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        onClick={() => setSelected(null)}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-obsidian/95 p-6 backdrop-blur-md"
                    >
                        <button
                            data-testid="gallery-lightbox-close"
                            onClick={() => setSelected(null)}
                            className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors hover:bg-gold/10"
                            aria-label="Close photo"
                        >
                            <X size={18} />
                        </button>
                        <motion.figure
                            initial={{ scale: 0.92, y: 24 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.94, y: 12 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-center"
                        >
                            <img
                                src={selected.src}
                                alt={selected.alt}
                                className="mx-auto max-h-[78vh] max-w-full rounded-xl border border-gold/40 object-contain shadow-[0_0_80px_rgba(184,134,11,0.2)]"
                            />
                            <figcaption className="mt-5 font-royal text-lg italic text-sand">
                                {selected.caption}
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
